/**
 * BroadcastingGuidePage — plain instructions for the people actually doing it.
 *
 * Written for a church media volunteer with a phone, not for an engineer. The
 * settings are deliberately modest: on a Zimbabwean mobile connection a steady
 * 720p picture that never drops is worth more than a 1080p one that stalls
 * every few minutes, and a congregation watching on phones cannot tell the
 * difference anyway.
 *
 * Route: /help/broadcasting  (anchors: #browser #phone-app #obs #external)
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Video, Smartphone, MonitorPlay, Globe, ArrowLeft, Wifi, CheckCircle2,
} from 'lucide-react';

const Step = ({ n, children }) => (
  <li className="flex gap-3">
    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full
                     bg-slate-900 text-[11px] font-semibold text-white">{n}</span>
    <span className="text-sm leading-relaxed text-slate-700">{children}</span>
  </li>
);

const Section = ({ id, icon: Icon, title, blurb, children, testId }) => (
  <section id={id} className="scroll-mt-20 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6"
           data-testid={testId}>
    <div className="flex items-start gap-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <h2 className="text-base font-semibold text-slate-900 md:text-lg">{title}</h2>
        <p className="mt-1 text-sm text-slate-500">{blurb}</p>
      </div>
    </div>
    <div className="mt-4">{children}</div>
  </section>
);

const Settings = ({ rows }) => (
  <div className="mt-4 overflow-hidden rounded-lg border border-slate-200">
    <table className="w-full text-sm">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} className="border-b border-slate-100 last:border-0">
            <th className="w-2/5 bg-slate-50 px-3 py-2 text-left font-medium text-slate-600">{k}</th>
            <td className="px-3 py-2 font-mono text-xs text-slate-800">{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const BroadcastingGuidePage = () => (
  <div className="mx-auto max-w-3xl space-y-5 p-4 sm:p-6" data-testid="broadcasting-guide">
    <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
      <ArrowLeft className="h-3.5 w-3.5" />Admin
    </Link>

    <header>
      <h1 className="text-3xl font-bold text-slate-900 sm:text-4xl">Going live</h1>
      <p className="mt-2 text-base text-slate-600">
        Four ways to broadcast a service, a conference session or a lecture.
        Start with the phone browser — it needs nothing installed and works from
        the back of the hall.
      </p>
    </header>

    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="guide-connectivity">
      <p className="flex items-start gap-2 text-sm text-amber-900">
        <Wifi className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          <strong>On a mobile connection, choose steady over sharp.</strong> A
          720p picture at 2000 kbps that never drops is worth far more to a
          congregation watching on phones than 1080p that freezes every few
          minutes. If the picture keeps stalling, lower the bitrate before
          anything else.
        </span>
      </p>
    </div>

    <Section id="browser" icon={Video} title="From a phone or laptop browser"
             testId="guide-browser"
             blurb="Nothing to install. Best for one camera and a volunteer who is not technical.">
      <ol className="space-y-3">
        <Step n={1}>Sign in to FWM on the phone and open the service, event or lesson.</Step>
        <Step n={2}>Choose <strong>Broadcast from Browser</strong>.</Step>
        <Step n={3}>Allow the camera and microphone when the phone asks. On an
          iPad, use Safari — Chrome on iPad will not always hand over the camera.</Step>
        <Step n={4}>Run the check it offers: it confirms the camera, that the
          microphone is hearing something, and that the server is reachable.</Step>
        <Step n={5}>Press <strong>Start broadcasting</strong>, then open the public
          page on another device to see what the congregation sees.</Step>
        <Step n={6}>Press <strong>Stop</strong> when the service ends. The page
          updates by itself.</Step>
      </ol>
      <p className="mt-4 flex items-start gap-2 text-sm text-slate-600">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
        Lock the phone in landscape, plug it into power, and stand it on something
        solid. An hour of handheld video is an hour of seasickness.
      </p>
    </Section>

    <Section id="phone-app" icon={Smartphone} title="From a phone broadcasting app"
             testId="guide-phone-app"
             blurb="A steadier picture and better sound than the browser, from the same phone.">
      <ol className="space-y-3">
        <Step n={1}>Install <strong>Larix Broadcaster</strong> (free, on both
          Android and iPhone). Confirm it is still the current version before a
          service — these apps change.</Step>
        <Step n={2}>In FWM, open the broadcast and choose <strong>Connect Phone App</strong>.</Step>
        <Step n={3}>Copy the <strong>Server</strong> address with the copy button.</Step>
        <Step n={4}>Copy the <strong>Stream key</strong>. You can copy it without
          showing it — handy when the laptop is on a projector.</Step>
        <Step n={5}>In Larix: Settings → Connections → New connection, paste the
          server address, paste the key, save.</Step>
        <Step n={6}>Set the camera and microphone, press the red button, and check
          the FWM page on another device.</Step>
      </ol>
      <Settings rows={[
        ['Resolution', '1280×720'],
        ['Frame rate', '30 fps (25 is fine)'],
        ['Video bitrate', '2000 kbps on mobile data, 3500 on good Wi-Fi'],
        ['Audio bitrate', '128 kbps'],
        ['Keyframe interval', '2 seconds'],
      ]} />
    </Section>

    <Section id="obs" icon={MonitorPlay} title="From OBS Studio"
             testId="guide-obs"
             blurb="For several cameras, slides, lower thirds and a proper sound desk.">
      <ol className="space-y-3">
        <Step n={1}>Install OBS Studio on the media laptop (obsproject.com).</Step>
        <Step n={2}>In FWM, open the broadcast and choose <strong>Connect OBS</strong>.</Step>
        <Step n={3}>In OBS: Settings → Stream → Service <strong>Custom…</strong>,
          then paste the server address and the stream key from FWM.</Step>
        <Step n={4}>Settings → Output → set the bitrate from the table below, and
          keyframe interval 2.</Step>
        <Step n={5}>Add your sources — camera, slides, and the audio from the
          sound desk rather than the laptop microphone.</Step>
        <Step n={6}>Press <strong>Start Streaming</strong>, then confirm on the
          FWM page that the picture has arrived before the service begins.</Step>
        <Step n={7}>Press <strong>Stop Streaming</strong> at the end, and end the
          broadcast in FWM.</Step>
      </ol>
      <Settings rows={[
        ['Output mode', 'Advanced'],
        ['Encoder', 'Hardware (NVENC / QuickVideo) if offered, otherwise x264'],
        ['Rate control', 'CBR'],
        ['Video bitrate', '2500 kbps at 720p, 4500 at 1080p'],
        ['Keyframe interval', '2 seconds'],
        ['Audio bitrate', '160 kbps'],
      ]} />
      <p className="mt-3 text-sm text-slate-600">
        If FWM says OBS cannot be used yet, the streaming server is not running
        the RTMP ingest service. Use <Link to="#external" className="underline">an
        external provider</Link> in the meantime — the broadcast still appears on
        the FWM page.
      </p>
    </Section>

    <Section id="external" icon={Globe} title="Through YouTube, Facebook or Restream"
             testId="guide-external"
             blurb="They carry the broadcast and keep the replay. The FWM page shows their player.">
      <ol className="space-y-3">
        <Step n={1}>Create the live event in YouTube Studio, Facebook Live
          Producer or Restream, and copy <em>their</em> server address and key.</Step>
        <Step n={2}>Put those into OBS or the phone app instead of FWM's.</Step>
        <Step n={3}>Copy the public watch link and paste it into the FWM
          broadcast's link box. FWM works out the provider by itself.</Step>
        <Step n={4}>Start streaming, then check the FWM page.</Step>
        <Step n={5}>Afterwards, the provider keeps the recording. Paste that same
          link back in and the page becomes the replay.</Step>
      </ol>
      <p className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
        <strong>One caution for restricted lectures.</strong> A YouTube or
        Facebook video that is not private on their side is not made private by
        sitting behind an FWM login — the link still works for anybody who has
        it. For a genuinely closed class, broadcast through FWM itself.
      </p>
    </Section>
  </div>
);

export default BroadcastingGuidePage;
